import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Leaf,
  UserPlus,
  Search,
  FileText,
  TrendingDown,
  Award,
  Recycle,
  Package,
  CalendarCheck,
  Send,
  Loader2,
  Calendar,
} from 'lucide-react';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1920&h=900&fit=crop';

const STEP_ICONS = [
  <UserPlus className="h-8 w-8 text-orange-500" />,
  <Search className="h-8 w-8 text-orange-500" />,
  <FileText className="h-8 w-8 text-orange-500" />,
];

const BENEFIT_ICONS = [
  <TrendingDown className="h-8 w-8 text-emerald-500" />,
  <ShieldCheck className="h-8 w-8 text-blue-500" />,
  <Recycle className="h-8 w-8 text-teal-500" />,
];

// Calendly inline widget — loads the Calendly embed script
const CALENDLY_URL = 'https://calendly.com/surplify-demo/30min'; // Placeholder — update in admin settings

function CalendlyWidget() {
  useEffect(() => {
    // Load Calendly CSS
    const link = document.createElement('link');
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Load Calendly JS
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.head.removeChild(link);
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div
      className="calendly-inline-widget rounded-xl overflow-hidden border shadow-sm"
      data-url={CALENDLY_URL}
      style={{ minWidth: '320px', height: '660px' }}
      data-testid="calendly-widget"
    />
  );
}

export default function ForPartnersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
      toast.error(t('demo.requiredFields'));
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/demo-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      toast.success(t('demo.successToast'));
    } catch {
      toast.error(t('demo.errorToast'));
    } finally {
      setSending(false);
    }
  };

  const steps = [
    { icon: STEP_ICONS[0], title: t('partners.step1Title'), desc: t('partners.step1Desc') },
    { icon: STEP_ICONS[1], title: t('partners.step2Title'), desc: t('partners.step2Desc') },
    { icon: STEP_ICONS[2], title: t('partners.step3Title'), desc: t('partners.step3Desc') },
  ];

  const benefits = [
    { icon: BENEFIT_ICONS[0], title: t('partners.benefit1Title'), desc: t('partners.benefit1Desc'), accent: 'emerald' },
    { icon: BENEFIT_ICONS[1], title: t('partners.benefit2Title'), desc: t('partners.benefit2Desc'), accent: 'blue' },
    { icon: BENEFIT_ICONS[2], title: t('partners.benefit3Title'), desc: t('partners.benefit3Desc'), accent: 'teal' },
  ];

  const stats = [
    { value: t('partners.trustStat1'), label: t('partners.trustStat1Label') },
    { value: t('partners.trustStat2'), label: t('partners.trustStat2Label') },
    { value: t('partners.trustStat3'), label: t('partners.trustStat3Label') },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="for-partners-page">
      <Header />

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section
        className="relative min-h-[88vh] flex items-center bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        data-testid="partners-hero"
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/80 to-slate-900/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            {/* Eyebrow badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-400/30 text-orange-300 text-xs font-semibold tracking-wider uppercase mb-6">
              <Package className="h-3.5 w-3.5" />
              Surplify for Partners
            </div>

            <h1
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight mb-6"
              data-testid="partners-hero-title"
            >
              {t('partners.heroTitle')}
            </h1>

            <p className="text-slate-300 text-base sm:text-lg leading-relaxed mb-10 max-w-xl">
              {t('partners.heroSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-6 text-base rounded-xl gap-2 shadow-lg shadow-orange-500/25 transition-all hover:scale-105"
                onClick={() => navigate('/register/external')}
                data-testid="partners-cta-register"
              >
                {t('partners.ctaRegister')}
                <ArrowRight className="h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 hover:border-white/50 font-semibold px-8 py-6 text-base rounded-xl transition-all"
                onClick={() => navigate('/search')}
                data-testid="partners-cta-browse"
              >
                {t('partners.ctaBrowse')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-orange-400/40 text-orange-300 hover:bg-orange-500/10 hover:border-orange-400/60 font-semibold px-8 py-6 text-base rounded-xl transition-all gap-2"
                onClick={() => document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="partners-cta-demo"
              >
                <CalendarCheck className="h-5 w-5" />
                {t('demo.ctaHero')}
              </Button>
            </div>

            {/* Trust micro-badges */}
            <div className="flex flex-wrap gap-4 mt-10">
              {[t('partners.trust1'), t('partners.trust2'), t('partners.trust3')].map((item, i) => (
                <span key={i} className="flex items-center gap-1.5 text-slate-300/80 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* ── STATS STRIP ─────────────────────────────────────────── */}
      <section className="bg-slate-900 dark:bg-slate-950 border-y border-slate-800" data-testid="partners-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-3 gap-8 text-center">
            {stats.map((stat, i) => (
              <div key={i} data-testid={`partners-stat-${i}`}>
                <p className="text-3xl sm:text-4xl font-extrabold text-orange-400 font-manrope">{stat.value}</p>
                <p className="text-slate-400 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ────────────────────────────────────────── */}
      <section className="py-20 bg-background" data-testid="partners-how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 rounded-full bg-orange-500/10 border border-orange-400/20 text-orange-600 dark:text-orange-400 text-xs font-semibold uppercase tracking-wider mb-4">
              {t('partners.howItWorksLabel')}
            </span>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold mb-4">
              {t('partners.howItWorksTitle')}
            </h2>
            <p className="text-muted-foreground text-base max-w-xl mx-auto">
              {t('partners.howItWorksSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {/* Connector line (desktop only) */}
            <div className="hidden md:block absolute top-14 left-[calc(33.3%+1rem)] right-[calc(33.3%+1rem)] h-px bg-gradient-to-r from-orange-500/30 via-orange-500/60 to-orange-500/30" />
            <div className="hidden md:block absolute top-14 left-[calc(66.6%-1rem)] right-[calc(16.7%+1rem)] h-px bg-gradient-to-r from-orange-500/30 via-orange-500/60 to-orange-500/30" />

            {steps.map((step, i) => (
              <div
                key={i}
                className="relative flex flex-col items-center text-center p-8 rounded-2xl bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 hover:border-orange-300 dark:hover:border-orange-800 transition-colors group"
                data-testid={`partners-step-${i + 1}`}
              >
                {/* Step number */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-orange-500/30">
                  {i + 1}
                </div>

                <div className="mt-4 mb-5 p-4 rounded-2xl bg-orange-50 dark:bg-orange-900/20 group-hover:bg-orange-100 dark:group-hover:bg-orange-900/30 transition-colors">
                  {step.icon}
                </div>
                <h3 className="font-manrope text-lg font-bold mb-3">{step.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ────────────────────────────────────────────── */}
      <section className="py-20 bg-slate-50 dark:bg-slate-950/60" data-testid="partners-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold mb-4">
              {t('partners.benefitsTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((b, i) => (
              <div
                key={i}
                className={`p-8 rounded-2xl border-2 transition-all hover:-translate-y-1 hover:shadow-xl ${
                  b.accent === 'emerald'
                    ? 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/20 border-emerald-200 dark:border-emerald-800/50 hover:border-emerald-400 dark:hover:border-emerald-600'
                    : b.accent === 'blue'
                    ? 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/20 border-blue-200 dark:border-blue-800/50 hover:border-blue-400 dark:hover:border-blue-600'
                    : 'bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-950/30 dark:to-emerald-950/20 border-teal-200 dark:border-teal-800/50 hover:border-teal-400 dark:hover:border-teal-600'
                }`}
                data-testid={`partners-benefit-${i + 1}`}
              >
                <div className="mb-5">{b.icon}</div>
                <h3 className="font-manrope text-xl font-bold mb-3">{b.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST / SOCIAL PROOF ────────────────────────────────── */}
      <section className="py-16 bg-background" data-testid="partners-trust">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-900/50">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/20">
                <Award className="h-8 w-8 text-orange-500" />
              </div>
              <div>
                <p className="font-manrope font-bold text-lg">{t('partners.trustTitle')}</p>
                <p className="text-muted-foreground text-sm mt-0.5">{t('partners.trustSubtitle')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800 border-2 border-white dark:border-slate-900 -ml-2 first:ml-0 flex items-center justify-center text-xs font-bold text-slate-600 dark:text-slate-300"
                >
                  {String.fromCharCode(65 + i)}
                </div>
              ))}
              <span className="ml-2 text-sm text-muted-foreground">{t('partners.trustUsers')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEMO BOOKING ─────────────────────────────────────────── */}
      <section
        className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden"
        data-testid="partners-demo-booking"
        id="demo"
      >
        {/* Decorative blobs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — Copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-400/30 text-orange-300 text-xs font-semibold tracking-wider uppercase mb-6">
                <CalendarCheck className="h-3.5 w-3.5" />
                {t('demo.badge')}
              </div>
              <h2 className="font-manrope text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-6 leading-tight">
                {t('demo.title')}
              </h2>
              <p className="text-slate-300 text-base sm:text-lg mb-8 leading-relaxed max-w-lg">
                {t('demo.subtitle')}
              </p>
              <div className="space-y-3">
                {[t('demo.point1'), t('demo.point2'), t('demo.point3')].map((p, i) => (
                  <span key={i} className="flex items-center gap-2 text-slate-300/90 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                    {p}
                  </span>
                ))}
              </div>
            </div>

            {/* Right — Form */}
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8" data-testid="demo-booking-form-card">
              {sent ? (
                <div className="text-center py-8" data-testid="demo-booking-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                  </div>
                  <h3 className="text-white font-manrope text-xl font-bold mb-3">{t('demo.successTitle')}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed max-w-sm mx-auto">{t('demo.successDesc')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="demo-booking-form">
                  <div>
                    <Label htmlFor="demo-name" className="text-slate-200 text-sm font-medium mb-1.5 block">{t('demo.name')} *</Label>
                    <Input
                      id="demo-name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder={t('demo.namePlaceholder')}
                      required
                      className="bg-white/10 border-white/15 text-white placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400/30"
                      data-testid="demo-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="demo-email" className="text-slate-200 text-sm font-medium mb-1.5 block">{t('demo.email')} *</Label>
                    <Input
                      id="demo-email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder={t('demo.emailPlaceholder')}
                      required
                      className="bg-white/10 border-white/15 text-white placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400/30"
                      data-testid="demo-input-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="demo-company" className="text-slate-200 text-sm font-medium mb-1.5 block">{t('demo.company')} *</Label>
                    <Input
                      id="demo-company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder={t('demo.companyPlaceholder')}
                      required
                      className="bg-white/10 border-white/15 text-white placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400/30"
                      data-testid="demo-input-company"
                    />
                  </div>
                  <div>
                    <Label htmlFor="demo-phone" className="text-slate-200 text-sm font-medium mb-1.5 block">{t('demo.phone')}</Label>
                    <Input
                      id="demo-phone"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={t('demo.phonePlaceholder')}
                      className="bg-white/10 border-white/15 text-white placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400/30"
                      data-testid="demo-input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="demo-message" className="text-slate-200 text-sm font-medium mb-1.5 block">{t('demo.message')}</Label>
                    <Textarea
                      id="demo-message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder={t('demo.messagePlaceholder')}
                      rows={3}
                      className="bg-white/10 border-white/15 text-white placeholder:text-slate-400 focus:border-orange-400 focus:ring-orange-400/30 resize-none"
                      data-testid="demo-input-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={sending}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 text-base rounded-xl gap-2 shadow-lg shadow-orange-500/25 transition-all hover:scale-[1.02] disabled:opacity-60"
                    data-testid="demo-submit-btn"
                  >
                    {sending ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> {t('demo.sending')}</>
                    ) : (
                      <><Send className="h-5 w-5" /> {t('demo.submit')}</>
                    )}
                  </Button>
                  <p className="text-slate-500 text-xs text-center">{t('demo.privacy')}</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CALENDLY BOOKING SECTION ─────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-background" data-testid="calendly-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 dark:bg-orange-900/20 text-orange-600 text-xs font-semibold tracking-wider uppercase mb-4">
              <Calendar className="h-3.5 w-3.5" />
              Online-Termin buchen
            </div>
            <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Persönliche Demo vereinbaren
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl mx-auto">
              Wählen Sie direkt einen passenden Termin in unserem Kalender. Wir zeigen Ihnen die Plattform live und beantworten alle Fragen.
            </p>
          </div>
          <CalendlyWidget />
        </div>
      </section>

      <Footer />
    </div>
  );
}
